// Dependencies
import { languages, ISOlanguages } from '@/router/globals.js'
import { HTTP, endpoints } from '@/http-common.js'
import moment from 'moment'

// Components
import CodeDiffViewer from '@jafri/vue-diff-view'
import Datepicker from 'vuejs-datepicker'
import Timepicker from 'vue2-timepicker'
import Loader from '@/components/partials/Loader.vue'
import ImageUpload from '@/components/editor/ImageUpload.vue'
import FileUpload from '@/components/editor/FileUpload.vue'
import ErrorPrompt from '@/components/editor/ErrorPrompt.vue'
import SuccessPrompt from '@/components/editor/SuccessPrompt.vue'
import SearchTag from '@/components/editor/blocks/SearchTag.vue'
import SearchAuthor from '@/components/editor/blocks/SearchAuthor.vue'

// Mixins
import GetTranslation from '@/components/mixins/GetTranslation.js'
import FilterByProp from '@/components/mixins/FilterByProp.js'
import ReduceItemData from '@/components/mixins/ReduceItemData.js'
import DisableItem from '@/components/mixins/DisableItem.js'
import GetProperty from '@/components/mixins/GetProperty.js'
import ManageTranslations from '@/components/mixins/ManageTranslations.js'
import Capitalize from '@/components/mixins/Capitalize.js'
import UpdatePost from '@/components/mixins/UpdatePost.js'

const EditorDefaults = {
  components: {
    CodeDiffViewer,
    Datepicker,
    Timepicker,
    Loader,
    ImageUpload,
    FileUpload,
    ErrorPrompt,
    SuccessPrompt,
    SearchTag,
    SearchAuthor,
  },
  props: ['searchParams', 'types'],
  mixins: [
    GetTranslation,
    ReduceItemData,
    FilterByProp,
    DisableItem,
    GetProperty,
    ManageTranslations,
    Capitalize,
    UpdatePost,
  ],
  data() {
    return {
      item: {
        author: '',
        api_keys: [],
        type: '',
        tag_ids: [],
        authors: [],
        authors_id: [],
        tags: [],
        status: 'published',
        password: null,
        l10n: [
          {
            title: '',
            status: 'published',
            lang: this.searchParams && this.searchParams.lang,
            description: '',
            foreword: '',
            contents: [],
            external_url: '',
            source_name: '',
            custom_fields: [],
          },
        ],
        contents: {
          thumbnail_urls: {
            card: '',
            event: '',
            featured: '',
            large: '',
            original: '',
            publication_cover: '',
          },
          thumbnail_url: '',
          location: {
            latitude: 0,
            longitude: 0,
          },
        },
        start_datetime: moment().toDate(),
        end_datetime: moment().toDate(),
        is_featured: false,
      },
      prompt: {
        save: 'Save',
        success: '',
      },
      parent: null,
      statuses: [...this.$store.state.types.statuses],
      taxonomies: [
        { name: 'issue', icon: 'tag', data: [] },
        { name: 'sector', icon: 'bullseye', data: [] },
        { name: 'organisation', icon: 'users', data: [] },
        { name: 'country', icon: 'map', data: [] },
        { name: 'region', icon: 'globe', data: [] },
        { name: 'campaign', icon: 'cog', data: [] },
        { name: 'type', icon: 'archive', data: [] },
        { name: 'clause_theme', icon: 'archive', data: [] },
        { name: 'clause_type', icon: 'archive', data: [] },
      ],
      type: 'article',
      scraperData: {
        type: 'article',
        scraped: true,
      },
      file: {},
      fileData: '',
      loading: true,
      updating: false,
      updated: false,
      changed: false,
      errors: [],
    }
  },
  watch: {
    item: {
      handler() {
        this.changed = true
        this.errors = []
      },
      deep: true,
    },
    // If document URL is pasted and extension is extracted, enable fields by assigning the type from extension. Extension has to be of valid type (pdf, ppt, doc, xls)
    externalURL() {
      this.checkUploadTypes()
    },
    // Sync thumbnail_urls with the deprecated thumbnail_url prop
    thumbs() {
      if (this.thumbs) {
        this.item.contents.thumbnail_url = this.item.contents.thumbnail_urls.original
      }
    },
  },
  created() {
    // Define translations and taxonomies upon creation
    this.setDefaults()

    // Set default type if listing contains more than one
    if (this.types.length > 1) {
      this.item.type = ''
    }

    // Check for edit prompt
    if (this.$route.params.id !== undefined) {
      this.prompt.save = 'Save'
      this.prompt.success = 'Success! Resource updated.'

      // Get the post, or actually post to get the post. Whatever.
      HTTP.post(`resource/${this.$route.params.id}`, {
        api_keys: this.$store.state.accounts.api_keys,
        is_backend: true,
      })
        .then((response) => {
          this.item = response.data
          // Populate api_keys from websites, set other defaults
          this.item.api_keys = response.data.websites.map((website) => website.api_key)
          this.status = response.data.status
          this.$router.push({
            query: { ...this.$route.query, lang: this.lang },
          })

          // Redefine basic data
          this.setDefaults()

          this.loading = false
        })
        .catch((e) => {
          this.errors = e.response ? e.response.data : console.error(e)
          this.loading = false
        })
      // Prepare data for 'add' prompt
    } else {
      this.scraperData.scraped = false
      this.$router.push({ query: { ...this.$route.query, lang: this.lang } })
      this.prompt.save = 'Create'
      this.prompt.success = 'Success! Resource created.'
      this.loading = false
      this.item.api_keys = this.searchParams.api_keys
      this.item.author = this.$store.state.accounts.user
      this.item.authors = [this.$store.state.accounts.user]
      this.item.authors_id = [this.$store.state.accounts.user.id]
      this.item.author_id = this.$store.state.accounts.user.id
    }
    // Define parent URL
    this.parent = {
      path: this.$route.matched.find((x) => x.parent !== undefined).parent.path,
      query: {
        search: JSON.stringify(this.searchParams),
      },
    }
  },
  computed: {
    filteredTaxonomies() {
      if (!['clause', 'page'].includes(this.item.type))
        return this.taxonomies.filter((t) => !['clause_type', 'clause_theme'].includes(t.name))
      return this.taxonomies
    },
    endpoint() {
      return endpoints.baseURL
    },
    scrappable() {
      let checker = true
      const arr = ['dailymotion', 'flickr', 'radiolabour', 'soundcloud', 'vimeo', 'youtu']
      arr.forEach((item) => {
        if (this.l10n[this.lang].external_url && this.l10n[this.lang].external_url.includes(item)) checker = false
      })

      if (
        (this.l10n[this.lang].external_url && this.l10n[this.lang].external_url.length === 0) ||
        (this.scraperData.type === 'media' && checker)
      ) {
        return true
      } else {
        return false
      }
    },
    languageStatuses() {
      return this.statuses.filter((status) => status !== 'password_protected' && status !== 'unlisted')
    },
    // Return lang data from URL query, if query exists
    lang() {
      if (this.$route.query.lang !== undefined) {
        return this.$route.query.lang
        //  If not, retrieve language from search parameters
      } else {
        return this.searchParams.lang
      }
    },
    langs() {
      return ISOlanguages
    },
    langLabels() {
      return languages
    },
    websites() {
      return JSON.parse(JSON.stringify(this.$store.state.accounts.org.websites))
    },
    previewLinks() {
      return this.websites.filter(
        (site) =>
          this.item.api_keys.includes(site.api_key) &&
          site.additionals.website_urls &&
          site.additionals.website_urls.length > 0 &&
          site.additionals.website_urls.find((url) => url.name.includes(this.item.type))
      )
    },
    disableUpdate() {
      if (this.$route.name !== 'Edit submission') {
        return this.updated || !this.item.authors_id.length
      } else {
        return this.updated
      }
    },
    externalURL() {
      if (this.l10n[this.lang] && this.l10n[this.lang].external_url) {
        return this.l10n[this.lang].external_url
      }
      return null
    },
    thumbs() {
      if (this.item.contents.thumbnail_urls) {
        return this.item.contents.thumbnail_urls
      }
      return null
    },
    uploadableTypes() {
      return ['doc', 'pdf', 'ppt', 'xls', 'archive_doc', 'audio', 'video', 'image', 'archive_mm']
    },
    enabled() {
      if (this.$store.state.accounts.org.websites && this.$store.state.websites.current[0]) {
        const currentApi = this.$store.state.websites.current[0]

        const current = this.$store.state.accounts.org.websites.find((website) => website.api_key === currentApi)
        if (current && current.additionals.defaults) return current.additionals.defaults
      }

      return true
    },
    flags() {
      let flags = []
      this.websites
        .filter((site) => this.item.api_keys.includes(site.api_key))
        .forEach((w) => {
          if (w.additionals.post_flags)
            flags = [...flags, ...(w.additionals.post_flags ? w.additionals.post_flags.split(',') : [])]
        })

      return flags
    },
    pageTemplates() {
      let templates = []
      this.websites
        .filter((site) => this.item.api_keys.includes(site.api_key))
        .forEach((w) => {
          if (w.additionals.page_templates)
            templates = [...templates, ...(w.additionals.page_templates ? w.additionals.page_templates.split(',') : [])]
        })

      return templates
    },
  },
  methods: {
    // set affiliate space checked if org tag is added
    setAffiliateActive(taxonomyName) {
      const affiliateSpaceApi = '176d402d-1a1e-4bc6-9319-7d078c440974'
      if (taxonomyName === 'organisation' && !this.item.api_keys.includes(affiliateSpaceApi)) {
        this.item.api_keys.push(affiliateSpaceApi)
      }
    },

    // Remove thumbnail_url
    removeThumbURL() {
      this.item.contents.thumbnail_url = ''
    },

    // Check upload types
    checkUploadTypes() {
      let url = this.externalURL
      if (url && url.length > 0) url = url.replace(/\s/g, '').toLowerCase()

      if (url && this.scraperData.type === 'document') {
        this.item.type = url.slice(((url.lastIndexOf('.') - 1) >>> 0) + 2)
      } else if (url && this.scraperData.type === 'media') {
        const extension = url.slice(((url.lastIndexOf('.') - 1) >>> 0) + 2)
        if (extension === 'mp3') this.item.type = 'audio'
        if (['mp4', 'avi', 'webm'].includes(extension)) this.item.type = 'video'
        if (['png', 'jpg', 'jpeg', 'webp'].includes(extension)) {
          this.item.type = 'image'
        }
      }
    },

    // Defaults
    setDefaults() {
      this.transformTranslationData(this.item.l10n)

      // Create layout builder fields prop if it doesn't exist
      Object.keys(this.l10n).forEach((l) => {
        // Create layout builder fields prop if it doesn't exist
        if (this.l10n[l].contents === undefined) this.$set(this.l10n[l], 'contents', [])

        // Create custom fields prop if it doesn't exist
        if (this.l10n[l].custom_fields === undefined) this.$set(this.l10n[l], 'custom_fields', [])
      })

      // Add empty text field upon creating new posts by default
      if (
        (this.enabled === true || this.enabled.general_options.start_with_text) &&
        this.item.l10n &&
        this.item.l10n.every(
          (item) => item && item.contents && item.contents.length === 0 && item.custom_fields.length === 0
        )
      ) {
        this.l10n[this.lang].contents.push({ type: 'text', text: '' })
      }

      // Uploadable types, as defined in back-end
      const documentTypes = ['doc', 'pdf', 'ppt', 'xls', 'archive_doc']
      const mediaTypes = ['audio', 'video', 'image', 'archive_mm']

      const merged = []
      const allTypes = merged.concat(documentTypes, mediaTypes)

      // Set default type
      if (this.types.length < allTypes.length) {
        if (this.item.type === 'audio') {
          this.item.type = 'audio'
        } else if (this.item.type === 'video') {
          this.item.type = 'video'
        } else if (this.item.type === 'image') {
          this.item.type = 'image'
        } else {
          this.item.type = this.types[0]
        }
      }

      // Default to media if types include media types
      if (documentTypes.includes(this.item.type)) {
        this.scraperData.type = 'document'

        // Default to media if types include media types
      } else if (mediaTypes.includes(this.item.type)) {
        this.scraperData.type = 'media'

        // Default to event if type is equal to event, add additional fields
      } else if (this.item.type === 'event') {
        this.scraperData.type = 'event'

        // Set defaults for new submission prompt
        if (this.$route.params.id === undefined) {
          this.l10n[this.lang].source = Object.assign({}, this.l10n[this.lang].source, {
            name: '',
            url: '',
          })
          this.$set(this.l10n[this.lang], 'location_info', '')
        }
      }

      // Set additionals object
      if (this.item.contents.additionals === undefined) {
        this.$set(this.item.contents, 'additionals', {})
      }

      const additionals = {
        post_flags: [],
        page_templates: [],
        hashtag: '',
        startTime: '',
        endTime: '',
        last_edited_user: [],
        timezone: '',
        virtual: false,
      }

      if (this.item.type === 'clause') {
        additionals.clause_properties = {
          class: '',
          class_full: '',
          id: '',
          internal_id: '',
          year: '',
          original_language: '',
          external_union: '',
        }
      }

      Object.keys(additionals).forEach((key) => {
        if (this.item.contents.additionals[key] === undefined) {
          this.$set(this.item.contents.additionals, key, additionals[key])
        }
      })

      // Set photo credits for posts
      if (this.item.contents.photo_credits === undefined) {
        this.$set(this.item.contents, 'photo_credits', '')
      }

      // Set post to unchanged if no data has been altered besides initial load
      setTimeout(() => {
        this.changed = false
      }, 100)
    },

    // Set date
    formatDate(value) {
      return moment(value).format('YYYY-MM-DD')
    },
    feature() {
      this.item.is_featured = !this.item.is_featured

      // Check if post has been published
      if (this.item.id !== undefined) {
        HTTP.post(`resource/update/${this.$route.params.id}`, {
          is_featured: this.item.is_featured,
          api_keys: this.item.api_keys,
        })
      }
    },

    // Sync tags from taxonomies to item.tag_ids
    syncTags() {
      const tags = this.taxonomies.flatMap((taxonomy) => taxonomy.data)
      this.item.tag_ids = tags
    },

    // The scraper
    scraper() {
      // Show loader
      this.updating = true

      // Define URL based on type
      let url = 'scraper/og'
      let body = { url: this.l10n[this.lang].external_url }

      if (this.scraperData.type === 'event') {
        url = `scraper/fb_event`
        body = { fb_url: this.l10n[this.lang].source.url }
      } else if (this.scraperData.type !== 'article') {
        url = `scraper/${this.scraperData.type}`
      }

      // Just scrape the darn thing
      HTTP.post(url, body)
        .then((response) => {
          this.scraperData.scraped = true

          let data = response.data
          if (this.scraperData.type === 'media') data = response.data[0]

          this.item.type = data.type
          const fields = ['title', 'description', 'source_name', 'external_url']
          fields.forEach((item) => {
            this.l10n[this.lang][item] = data[item]
          })

          // Upload image from scraper to 'uploader' endpoint if response is not null
          if (data.thumbnail_urls) {
            this.item.contents.thumbnail_urls = data.thumbnail_urls
          }

          // Check if facebook event
          if (this.scraperData.type === 'event') {
            // Update contents
            this.l10n[this.lang].source = Object.assign({}, this.l10n[this.lang].source, {
              name: data.title,
              url: data.external_url,
            })
            this.$set(this.item.contents, 'location', data.location)

            this.l10n[this.lang].location_info = data.location.city

            this.item.start_datetime = data.start_datetime
            this.item.end_datetime = data.end_datetime
          }
          this.updating = false
          this.loading = false
        })
        .catch((e) => {
          this.scraperData.scraped = true
          // this.l10n[this.lang].source_name = new URL(this.l10n[this.lang].external_url).hostname
          this.errors = e.response ? e.response.data : console.error(e)
          this.updating = false
          this.loading = false
        })
    },

    scrape() {
      // Check if post has already been scraperData.scraped, warn the user like OMG!
      if (this.scraperData.scraped === true) {
        if (confirm('Your title and description are not empty! Are you sure you want to re-scrape this resource?')) {
          this.scraper()
        }
        // No scrapy, pls scrape
      } else {
        this.scraper()
      }
    },

    // File upload
    readFile(event) {
      const input = event.target
      this.errors = []
      if (input.files && input.files[0]) {
        // Create a new FileReader to read file
        const reader = new FileReader()

        reader.onload = () => {
          this.file = input.files[0]
          this.uploader()
        }
        reader.readAsBinaryString(input.files[0])
      } else {
        this.errors = 'Something went wrong!'
      }
    },

    uploader() {
      this.updating = true

      let formData = new FormData()
      formData.append('file', this.file)

      HTTP.post(`upload`, formData)
        .then((response) => {
          // Load data, set type to scraper type
          this.updating = false

          // Populate with response
          this.l10n[this.lang].external_url = response.data.url
          this.item.type = response.data.type
        })
        .catch((e) => {
          this.errors = e.response ? e.response.data : console.error(e)
          this.updating = false
        })
    },

    closeModal() {
      if (this.changed === true) {
        if (confirm('Are you sure you wish to close the editor? Everything not saved will be lost!')) {
          this.$router.push(this.parent)
        }
      } else {
        this.$router.push(this.parent)
      }
    },

    previewLinkBase(website) {
      if (website.additionals.website_urls) {
        return website.additionals.website_urls.find((url) => url.name.includes(this.item.type)).link
      }
      return null
    },
  },
  mounted() {
    if (this.$store.state.accounts.user.is_admin || this.$store.state.accounts.user.is_org_admin) {
      this.statuses.push('password_protected')
    }
  },
}

export default EditorDefaults
